<template>
  <section class="statement-section">
    <div class="statement-section__header">
      <div class="statement-section__marker">8</div>
      <div>
        <h3 class="statement-section__title">Печать документов</h3>
        <p class="statement-section__description">
          Формирование документов по утвержденному заявлению
        </p>
      </div>
    </div>

    <div class="statement-subsection">
      <h4 class="statement-subsection__title">Печать заявления</h4>

      <div v-if="printableContests.length" class="statement-print-documents">
        <div v-for="contest in printableContests" :key="contest.key" class="statement-print-documents__row">
          <div class="statement-print-documents__text">
            <div class="statement-print-documents__title">
              {{ contest.group.name }} <span class="statement-muted">({{ contest.group.code }})</span>
            </div>
            <div class="statement-print-documents__meta">
              Приоритет {{ contest.priority }} · {{ getDepartmentName(contest.group.department_id) }} ·
              {{ contest.group.study_name }} · {{ getFundingLabel(contest.financing) }}
              <template v-if="getGroupQualificationLabel(contest.group)">
                · {{ getGroupQualificationLabel(contest.group) }}
              </template>
            </div>
          </div>

          <v-btn :href="getPrintApplicationUrl(contest)" target="_blank" rel="noopener" color="primary" variant="tonal"
            prepend-icon="mdi-printer" :disabled="!contest.attemptId">
            Печать
          </v-btn>
        </div>
      </div>

      <div v-else class="statement-empty">
        Нет сохраненных конкурсных групп для печати заявления.
      </div>
    </div>

    <div class="statement-subsection">
      <h4 class="statement-subsection__title">Печать договора</h4>

      <div v-if="printableContests.length" class="statement-print-documents">
        <div v-for="contest in printableContests" :key="`contract-${contest.key}`"
          class="statement-print-documents__row">
          <div class="statement-print-documents__text">
            <div class="statement-print-documents__title">
              {{ contest.group.name }} <span class="statement-muted">({{ contest.group.code }})</span>
            </div>
            <div class="statement-print-documents__meta">
              Приоритет {{ contest.priority }} · {{ contest.group.study_name }} · {{ getFundingLabel(contest.financing)
              }}
            </div>
          </div>

          <v-btn color="primary" variant="tonal" prepend-icon="mdi-printer"
            :disabled="!contest.attemptId || !formData.pa_id || existingMainContact" @click="openContractDialog(contest, 0)">
            Печать
          </v-btn>
        </div>
      </div>

      <div v-else class="statement-empty">
        Нет сохраненных конкурсных групп для печати договора.
      </div>
    </div>

    <div v-for="action in standaloneContractActions" :key="action.type" class="statement-subsection">
      <div class="statement-print-documents__row">
        <div class="statement-print-documents__text">
          <div class="statement-print-documents__title">{{ action.sectionTitle }}</div>
        </div>
      <v-btn color="primary" variant="tonal" :prepend-icon="action.icon" :disabled="!formData.pa_id"
        @click="openContractDialog(null, action.type)">
        Печать
      </v-btn>
      </div>
    </div>

    <div class="statement-subsection">
      <h4 class="statement-subsection__title">Существующие договоры</h4>

      <div v-if="existingContracts.length" class="statement-existing-contracts">
        <a v-for="contract in existingContracts" :key="contract.id" class="statement-existing-contracts__link"
          :href="getExistingContractUrl(contract)" target="_blank" rel="noopener">
          {{ getExistingContractLabel(contract) }}
        </a>
      </div>

      <div v-else class="statement-empty">
        Нет сохраненных договоров.
      </div>
    </div>

    <v-dialog v-model="contractDialog" max-width="800">
      <v-card>
        <v-card-title class="statement-contract-dialog-title">{{ contractDialogTitle }}</v-card-title>
        <v-card-text>
          <form ref="contractForm" :action="contractPrintUrl" method="post" target="_blank"
            class="statement-contract-form">
            <input type="hidden" name="save" value="1">
            <input type="hidden" name="type" :value="contractForm.type">
            <input type="hidden" name="student_id" :value="formData.pa_id || ''">
            <input type="hidden" name="psid" :value="contractForm.psid">
            <input type="hidden" name="date" :value="formatDateForContractSubmit(contractForm.date)">
            <input type="hidden" name="studyStart" :value="contractStudyPeriodReady ? formatDateForContractSubmit(contractForm.studyStart) : '0'">
            <input type="hidden" name="studyEnd" :value="contractStudyPeriodReady ? formatDateForContractSubmit(contractForm.studyEnd) : '0'">
            <input type="hidden" name="client_id" :value="contractForm.client_id || ''">
            <input type="hidden" name="obshaga_address" :value="contractForm.obshaga_address || ''">
            <input type="hidden" name="typeTemplate" :value="contractForm.typeTemplate || ''">
            <template v-if="isServicesContract">
              <template v-for="course in serviceCourses" :key="`hidden-${course.index}`">
                <input v-for="serviceId in contractForm[course.field]" :key="`${course.field}-${serviceId}`"
                  type="hidden" :name="`${course.field}[]`" :value="serviceId">
              </template>
            </template>

            <div class="statement-contract-grid">
              <v-text-field v-model="contractForm.contract_number" name="contract_number" label="Номер договора"
                variant="outlined" density="comfortable" autocomplete="one-time-code" />

              <v-text-field v-model="contractForm.date" type="date" label="Дата договора" placeholder="дд.мм.гггг"
                variant="outlined" density="comfortable" autocomplete="one-time-code" />

              <v-text-field v-model="contractForm.studyStart" type="date" label="Начало обучения"
                placeholder="дд.мм.гггг" variant="outlined" density="comfortable" autocomplete="one-time-code" />

              <v-text-field v-model="contractForm.studyEnd" type="date" label="Окончание обучения"
                placeholder="дд.мм.гггг" variant="outlined" density="comfortable" autocomplete="one-time-code" />
            </div>

            <div class="statement-contract-subtitle">Заказчик</div>

            <v-select v-model="contractForm.client_id" :items="clientOptions" item-title="name" item-value="value"
              label="Заказчик" variant="outlined" density="comfortable" clearable autocomplete="one-time-code"
              @update:model-value="applySelectedClient" />

            <v-text-field v-model="contractForm.client_name" name="client_name" label="ФИО" variant="outlined"
              density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />

            <div class="statement-contract-grid">
              <v-text-field v-model="contractForm.client_series" name="client_series" label="Серия" variant="outlined"
                density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />

              <v-text-field v-model="contractForm.client_number" name="client_number" label="Номер" variant="outlined"
                density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />
            </div>

            <v-textarea v-model="contractForm.client_passport" name="client_passport" label="Выдан" variant="outlined"
              density="comfortable" rows="2" auto-grow autocomplete="one-time-code" :readonly="clientSelected"
              :class="readonlyClientClass" />

            <div class="statement-contract-subtitle">Адрес регистрации</div>

            <div class="statement-contract-grid">
              <v-text-field v-model="contractForm.client_index" name="client_index" label="Индекс" variant="outlined"
                density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />
              <v-text-field v-model="contractForm.client_country" name="client_country" label="Страна"
                variant="outlined" density="comfortable" autocomplete="one-time-code" :readonly="clientSelected"
                :class="readonlyClientClass" />
              <v-text-field v-model="contractForm.client_subject" name="client_subject" label="Субъект"
                variant="outlined" density="comfortable" autocomplete="one-time-code" :readonly="clientSelected"
                :class="readonlyClientClass" />
              <v-text-field v-model="contractForm.client_village" name="client_village" label="Район" variant="outlined"
                density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />
              <v-text-field v-model="contractForm.client_city" name="client_city" label="Населенный пункт"
                variant="outlined" density="comfortable" autocomplete="one-time-code" :readonly="clientSelected"
                :class="readonlyClientClass" />
              <v-text-field v-model="contractForm.client_street" name="client_street" label="Улица" variant="outlined"
                density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />
              <v-text-field v-model="contractForm.client_house" name="client_house" label="Дом" variant="outlined"
                density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />
              <v-text-field v-model="contractForm.client_apartment" name="client_apartment" label="Квартира"
                variant="outlined" density="comfortable" autocomplete="one-time-code" :readonly="clientSelected"
                :class="readonlyClientClass" />
            </div>

            <v-text-field v-model="contractForm.client_phone" name="client_phone" label="Телефон" variant="outlined"
              density="comfortable" autocomplete="one-time-code" :readonly="clientSelected" :class="readonlyClientClass" />

            <v-select v-if="showHostelAddress" v-model="contractForm.obshaga_address" :items="hostelList"
              item-title="address" item-value="id" label="Адрес общежития" variant="outlined" density="comfortable"
              autocomplete="one-time-code" />

            <div class="statement-contract-grid">
              <v-text-field v-model="contractForm.price" name="price" label="Стоимость обучения" variant="outlined"
                density="comfortable" autocomplete="one-time-code" />

              <v-select v-model="contractForm.typeTemplate" :items="currentContractTemplates" item-title="name"
                item-value="id" label="Шаблон договора" variant="outlined" density="comfortable" autocomplete="one-time-code" />
            </div>

            <div v-if="isServicesContract" class="statement-contract-services">
              <div class="statement-contract-subtitle">Дополнительные услуги</div>

              <v-autocomplete v-for="course in serviceCourses" :key="course.field" v-model="contractForm[course.field]"
                :items="services" item-title="name" item-value="id" :label="course.label" variant="outlined"
                density="comfortable" multiple chips closable-chips autocomplete="one-time-code" />
            </div>
          </form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="contractDialog = false">Отмена</v-btn>
          <v-btn color="primary" variant="flat" prepend-icon="mdi-printer" @click="submitContractForm">
            {{ contractPrintButtonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
const emptyContractForm = () => ({
  type: 0,
  contract_number: '',
  date: '',
  studyStart: '',
  studyEnd: '',
  client_id: null,
  client_name: '',
  client_series: '',
  client_number: '',
  client_passport: '',
  client_index: '',
  client_country: '',
  client_subject: '',
  client_village: '',
  client_city: '',
  client_street: '',
  client_house: '',
  client_apartment: '',
  client_phone: '',
  obshaga_address: null,
  price: '',
  typeTemplate: null,
  psid: '',
  contract_type_1: [],
  contract_type_2: [],
  contract_type_3: [],
  contract_type_4: [],
  contract_type_5: []
})

export default {
  name: 'PrintDocumentsSection',
  props: {
    formData: {
      type: Object,
      required: true
    },
    selectedContestDetails: {
      type: Array,
      required: true
    },
    hostelList: {
      type: Array,
      default: () => []
    },
    contractTemplates: {
      type: Array,
      default: () => []
    },
    services: {
      type: Array,
      default: () => []
    },
    getDepartmentName: {
      type: Function,
      required: true
    },
    getFundingLabel: {
      type: Function,
      required: true
    },
    getGroupQualificationLabel: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      contractDialog: false,
      contractForm: emptyContractForm(),
      contractsRefreshTimer: null
    }
  },
  beforeUnmount() {
    if (this.contractsRefreshTimer) {
      window.clearTimeout(this.contractsRefreshTimer)
    }
  },
  computed: {
    printableContests() {
      return [...this.selectedContestDetails].sort(
        (a, b) => Number(a.priority) - Number(b.priority)
      )
    },
    standaloneContractActions() {
      return [
        {
          type: 1,
          sectionTitle: 'Печать договора на ДОУ',
          title: 'Договор на ДОУ',
          icon: 'mdi-printer'
        },
        {
          type: -1,
          sectionTitle: 'Печать договора о предоставления в найм жилого помещения',
          title: 'Договор о предоставления в найм жилого помещения',
          icon: 'mdi-printer'
        },
        {
          type: -2,
          sectionTitle: 'Печать договора об оказании дополнительных услуг студентам из числа проживающих в общежитии',
          title: 'Договор об оказании дополнительных услуг студентам из числа проживающих в общежитии',
          icon: 'mdi-printer'
        }
      ]
    },
    existingMainContact() {
      return this.existingContracts.some(contract => Number(contract.type) === 0)
    },
    existingContracts() {
      return Array.isArray(this.formData.existingContracts)
        ? this.formData.existingContracts
        : []
    },
    contractPrintUrl() {
      return `${this.$model.wwwroot}/blocks/manage_groups/admission_new/print_contract.php`
    },
    contractDialogTitle() {
      return this.contractTitleByType[Number(this.contractForm.type)] || 'Создать договор'
    },
    contractPrintButtonText() {
      return this.contractPrintTextByType[Number(this.contractForm.type)] || 'Печать договора'
    },
    contractDocumentByType() {
      return {
        0: 'contract',
        1: 'services',
        '-1': 'hostel',
        '-2': 'hostel_additional'
      }
    },
    contractTitleByType() {
      return {
        0: 'Создать договор',
        1: 'Создать договор на ДОУ',
        '-1': 'Создать договор о предоставления в найм жилого помещения',
        '-2': 'Создать договор об оказании дополнительных услуг студентам из числа проживающих в общежитии'
      }
    },
    contractPrintTextByType() {
      return {
        0: 'Печать договора',
        1: 'Печать договора на ДОУ',
        '-1': 'Печать договора найма',
        '-2': 'Печать договора доп. услуг'
      }
    },
    currentContractTemplates() {
      return this.getContractTemplatesByType(this.contractForm.type)
    },
    isServicesContract() {
      return Number(this.contractForm.type) === 1
    },
    contractStudyPeriodReady() {
      return Boolean(this.contractForm.studyStart && this.contractForm.studyEnd)
    },
    serviceCourses() {
      return [1, 2, 3, 4, 5].map(index => ({
        index,
        field: `contract_type_${index}`,
        label: `${index} курс`
      }))
    },
    clientSelected() {
      return Boolean(this.contractForm.client_id)
    },
    showHostelAddress() {
      if ([-1, -2].includes(Number(this.contractForm.type))) {
        return this.hostelList.length > 0
      }

      return this.formData.dormitoryNeed &&
        this.formData.dormitoryNeed !== 'no' &&
        this.hostelList.length > 0
    },
    readonlyClientClass() {
      return this.clientSelected ? 'statement-contract-field--readonly' : ''
    },
    clientOptions() {
      const options = []

      if (this.formData.pa_id) {
        options.push({
          value: `applicant_${this.formData.pa_id}`,
          name: this.getPersonName(this.formData),
          data: this.getApplicantClientData()
        })
      }

      const payers = Array.isArray(this.formData.payers) ? this.formData.payers : []
      payers.forEach(payer => {
        if (!payer.id || !this.getPersonName(payer)) {
          return
        }

        options.push({
          value: `p_${payer.id}`,
          name: this.getPersonName(payer),
          data: this.getPayerClientData(payer)
        })
      })

      return options
    }
  },
  methods: {
    getPrintApplicationUrl(contest) {
      return `${this.$model.wwwroot}/blocks/manage_groups/admission_new/print_application.php?attempt_id=${contest.attemptId}`
    },
    getExistingContractUrl(contract) {
      return `${this.contractPrintUrl}?id=${contract.id}&student_id=${this.formData.pa_id}`
    },
    getExistingContractLabel(contract) {
      return `${contract.name || 'Договор'} №${contract.number || ''} от ${this.formatContractDate(contract.date)}`
    },
    formatContractDate(value) {
      const date = String(value || '')

      if (!/^\d{8}$/.test(date)) {
        return date
      }

      return `${date.slice(6, 8)}.${date.slice(4, 6)}.${date.slice(0, 4)}`
    },
    formatDateForContractSubmit(value) {
      const date = String(value || '')

      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}`
      }

      if (/^\d{2}\.\d{2}\.\d{4}$/.test(date)) {
        return date
      }

      return ''
    },
    getContractTemplatesByType(type) {
      const document = this.contractDocumentByType[Number(type)]

      return this.contractTemplates.filter(template => template.document === document)
    },
    openContractDialog(contest = null, type = 0) {
      const defaultDate = this.getDefaultContractDate()
      const templates = this.getContractTemplatesByType(type)

      this.contractForm = {
        ...emptyContractForm(),
        type,
        date: defaultDate,
        studyStart: defaultDate,
        psid: contest && contest.attemptId ? contest.attemptId : '',
        price: contest && contest.group ? contest.group.price || '' : '',
        typeTemplate: templates.length ? templates[0].id : null,
        obshaga_address: this.hostelList.length ? this.hostelList[0].id : null
      }
      this.contractDialog = true
      this.loadContractNumber()
    },
    loadContractNumber() {
      return this.axios
        .post(`${this.$model.wwwroot}/procollege/manage_groups/admission/createApplicant/getContractNum`)
        .then((response) => {
          this.contractForm.contract_number = response.data || ''
        })
        .catch(() => {
          this.contractForm.contract_number = ''
        })
    },
    submitContractForm() {
      if (this.$refs.contractForm) {
        this.$refs.contractForm.submit()
        this.contractDialog = false
        this.scheduleExistingContractsRefresh()
      }
    },
    scheduleExistingContractsRefresh() {
      if (this.contractsRefreshTimer) {
        window.clearTimeout(this.contractsRefreshTimer)
      }

      this.contractsRefreshTimer = window.setTimeout(() => {
        this.contractsRefreshTimer = null
        this.loadExistingContracts()
      }, 1000)
    },
    loadExistingContracts() {
      if (!this.formData.pa_id) {
        return Promise.resolve(null)
      }

      const requestData = new FormData()
      requestData.append('personalId', this.formData.pa_id)

      return this.axios
        .post(
          `${this.$model.wwwroot}/procollege/manage_groups/admission/createApplicant/getExistingContracts`,
          requestData
        )
        .then((response) => {
          const data = response.data || {}
          const errors = Array.isArray(data.errors) ? data.errors : []

          if (!errors.length && Array.isArray(data.contracts)) {
            this.formData.existingContracts = data.contracts
          }

          return data
        })
        .catch(() => null)
    },
    applySelectedClient(value) {
      const selected = this.clientOptions.find(option => option.value === value)

      if (!selected) {
        this.clearClientFields()
        return
      }

      this.contractForm = {
        ...this.contractForm,
        ...selected.data
      }
    },
    clearClientFields() {
      this.contractForm = {
        ...this.contractForm,
        client_name: '',
        client_series: '',
        client_number: '',
        client_passport: '',
        client_index: '',
        client_country: '',
        client_subject: '',
        client_village: '',
        client_city: '',
        client_street: '',
        client_house: '',
        client_apartment: '',
        client_phone: ''
      }
    },
    getApplicantClientData() {
      const passport = this.formData.passport || {}

      return {
        client_name: this.getPersonName(this.formData),
        client_series: passport.series || '',
        client_number: passport.number || '',
        client_passport: passport.issuedBy || '',
        client_phone: this.formData.phone || '',
        ...this.getAddressData(this.formData.registrationAddress)
      }
    },
    getPayerClientData(payer) {
      const passport = payer.passport || {}

      return {
        client_name: this.getPersonName(payer),
        client_series: passport.series || '',
        client_number: passport.number || '',
        client_passport: passport.issuedBy || '',
        client_phone: payer.phone || '',
        ...this.getAddressData(payer.registrationAddress)
      }
    },
    getPersonName(person) {
      return [
        person.lastname,
        person.firstname,
        person.patronymic
      ].filter(Boolean).join(' ').trim()
    },
    getAddressData(address) {
      const data = address && address.data ? address.data : {}

      return {
        client_index: this.getAddressField(data, ['postal_code', 'index', 'ind']),
        client_country: this.getAddressField(data, ['country']),
        client_subject: this.getAddressField(data, ['region_with_type', 'region', 'subject']),
        client_village: this.getAddressField(data, ['area_with_type', 'area', 'village']),
        client_city: this.getAddressField(data, ['city_with_type', 'settlement_with_type', 'city']),
        client_street: this.getAddressField(data, ['street_with_type', 'street']),
        client_house: this.getAddressField(data, ['house']),
        client_apartment: this.getAddressField(data, ['flat', 'apartment'])
      }
    },
    getAddressField(data, keys) {
      const key = keys.find(item => data[item] !== null && data[item] !== undefined && String(data[item]).trim() !== '')

      return key ? data[key] : ''
    },
    getDefaultContractDate() {
      return `${new Date().getFullYear()}-09-01`
    }
  }
}
</script>

<style>
.statement-print-documents {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.statement-print-documents__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  background: #ffffff;
}

.statement-print-documents__text {
  min-width: 0;
}

.statement-print-documents__title {
  font-weight: 700;
  color: #1f2937;
  overflow-wrap: anywhere;
}

.statement-print-documents__meta {
  margin-top: 4px;
  color: #667085;
  line-height: 1.4;
}

.statement-contract-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.statement-contract-dialog-title {
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.3;
  padding-right: 24px;
}

.statement-contract-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.statement-contract-subtitle {
  margin-top: 4px;
  font-weight: 700;
  color: #1f2937;
}

.statement-existing-contracts {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.statement-existing-contracts__link {
  color: #1d4ed8;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.statement-contract-field--readonly .v-field {
  background-color: #f3f4f6;
}

.statement-contract-field--readonly .v-field__overlay {
  background-color: #f3f4f6;
  opacity: 1;
}

.statement-contract-field--readonly .v-field__input,
.statement-contract-field--readonly input,
.statement-contract-field--readonly textarea {
  color: #4b5563;
}

@media (max-width: 640px) {
  .statement-print-documents__row {
    grid-template-columns: minmax(0, 1fr);
  }

  .statement-contract-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
